import { ConnectorHosting } from "@p0security/integrations-app-shared/components";
import {
  newVaultElement,
  withoutAzureKeyVault,
} from "@p0security/integrations-vaulted-credential-shared";
import {
  Element,
  InstallSpec,
  ItemComponent,
  Option,
} from "@p0security/shared/install/types";

/** A 1Password account's sign-in address, which keys its install item: a
 * lowercase host in one of 1Password's regions, such as `acme.1password.com`. */
export const SIGN_IN_ADDRESS_PATTERN =
  /^[a-z0-9]([-a-z0-9]*[a-z0-9])?(\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*\.1password\.(com|ca|eu)$/;

export const INVALID_SIGN_IN_ADDRESS =
  "Enter the 1Password account's sign-in address, such as acme.1password.com: the address you sign in at, without https://.";

/** How many open requests a person may have in one account at a time. */
export const REQUEST_LIMITS = ["user", "vault", "item"] as const;
export type RequestLimit = (typeof REQUEST_LIMITS)[number];

// A connector's id names its Terraform resources, and each account picks its
// connector by it. The pattern and its 63-character limit are a DNS label's
// (RFC 1123), as Kubernetes names an object.
const CONNECTOR_ID_PATTERN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
const CONNECTOR_ID_MAX_LENGTH = 63;

export const INVALID_CONNECTOR_ID = `Use lowercase letters, digits and hyphens only, starting and ending with a letter or digit, up to ${CONNECTOR_ID_MAX_LENGTH} characters.`;

/** Where an account's service account token is kept: the customer's own AWS
 * Secrets Manager or Google Secret Manager, in the same cloud as the
 * account's connector. P0 stores where the secret is, never the token. */
export const OnePasswordTokenVault = withoutAzureKeyVault(
  newVaultElement({ owner: "account" })
);

/** Two items, and each artifact the install produces has one owner: the
 * deployed connector, its identity and P0's permission to invoke it belong to
 * `connector`; an account's token secret, and the connector's read on it, belong
 * to `account`.
 *
 * Copied from Docker Hub's install spec rather than shared, so that adding
 * 1Password changes no other integration. */
export const OnePasswordComponents = InstallSpec({
  // One item per deployed connector, keyed by an id the customer chooses. A
  // connector serves every account that points at it.
  //
  // The connector's identity isn't stored: its IAM role, or its service
  // account, is named after the connector (see `toConnectorIdentity`), as a
  // connector the customer deploys themselves must name it too.
  connector: ItemComponent({
    description:
      "Runs P0's 1Password connector in your AWS account or Google Cloud project. One connector can serve many 1Password accounts, or each account can have its own.",
    label: "Connector",
    maturity: "beta",
    schema: { hosting: ConnectorHosting },
    type: "string",
    validator: async (id) =>
      id.length <= CONNECTOR_ID_MAX_LENGTH && CONNECTOR_ID_PATTERN.test(id)
        ? undefined
        : INVALID_CONNECTOR_ID,
  }),
  // One item per 1Password account, keyed by its sign-in address. P0 lists
  // the vaults the account's service account can see, so the service
  // account's vault access in 1Password decides what's requestable.
  //
  // The address has no item validator here: the install framework's error for
  // one repeats the whole request, which may hold a token pasted in place of
  // its secret's name. The backend checks it instead (`SIGN_IN_ADDRESS_PATTERN`).
  account: ItemComponent({
    description:
      "A 1Password account whose vaults P0 can share items from, through one of your connectors and the account's service account",
    label: "Account",
    maturity: "beta",
    // The connector, the secret manager and the token's secret are fixed when
    // the item is created: the account's Terraform grants exactly that
    // connector read on exactly that secret. A rotated token goes into the
    // same secret, as a new version. The form asks for them in this order.
    schema: {
      connector: Element({
        description:
          "The connector that shares this account's items. P0 lists the 1Password connectors you've installed.",
        label: "Connector",
        step: "new",
        type: "dynamic",
      }),
      // Named for what it is, so it doesn't read as a 1Password vault.
      secretManager: OnePasswordTokenVault,
      tokenSecretName: Element({
        description:
          "The name, not the ARN or the full resource name, of the secret that holds, or will hold, the account's service account token. The account's Terraform lets the connector read exactly this secret.",
        label: "Service account token secret",
        step: "new",
        type: "string",
      }),
      // These two can change after install: they apply to requests and
      // links made after the change.
      oneTimeLinks: Element({
        description:
          "Each share link opens only once. The person gets one look at the item, so they should copy what they need. Applies to links made after you change it.",
        label: "One-time links",
        optional: true,
        step: "configure",
        type: "switch",
      }),
      requestLimit: Element({
        default: "item",
        description:
          "How many open requests a person may have at a time. To request again, they revoke their open request first.",
        label: "Request limit",
        options: {
          item: Option({
            label: "One per item",
            maturity: "beta",
            schema: {},
          }),
          vault: Option({
            label: "One per vault",
            maturity: "beta",
            schema: {},
          }),
          user: Option({
            label: "One in this account",
            maturity: "beta",
            schema: {},
          }),
        },
        step: "configure",
        type: "select",
      }),
      allowedDomains: Element({
        description:
          "The email domains the connector shares items with, as its DOMAIN_ALLOW_PATTERN read when the account was last validated. To change them, change the connector's deployment.",
        label: "Allowed domains",
        type: "generated",
      }),
    },
    type: "string",
  }),
});
